import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import ContactModal from "./ContactModal";
import { 
  Droplets, 
  Accessibility, 
  Home, 
  Utensils, 
  Bed, 
  Lightbulb,
  ArrowRight,
  CheckCircle,
  Euro,
  Phone,
  MessageCircle,
  Shield
} from "lucide-react";
import { Link } from "react-router-dom";
import { mockProjects } from "../utils/mockData";
import BoutonRappel from "./BoutonRappel";

const ProjectSelector = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const projectIcons = {
    "salle-de-bain": <Droplets className="h-12 w-12 text-blue-600" />,
    "accessibilite": <Accessibility className="h-12 w-12 text-green-700" />,
    "exterieur": <Home className="h-12 w-12 text-orange-700" />,
    "cuisine": <Utensils className="h-12 w-12 text-purple-600" />,
    "chambre": <Bed className="h-12 w-12 text-pink-600" />,
    "domotique": <Lightbulb className="h-12 w-12 text-yellow-600" />,
    "volets-roulants": <Shield className="h-12 w-12 text-indigo-600" />
  };

  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId);
    // Scroll to project details
    document.getElementById('project-details')?.scrollIntoView({ behavior: 'smooth' });
  };

  const selectedProjectData = mockProjects.find(p => p.id === selectedProject);

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Je choisis mon projet d'adaptation
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Sélectionnez le type d'adaptation qui vous intéresse pour découvrir nos solutions 
            et le financement possible avec MaPrimeAdapt'
          </p>
        </div>

        {/* Project Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {mockProjects.map((project) => (
            <Card 
              key={project.id}
              className={`cursor-pointer transition-all duration-300 hover:shadow-xl border-2 ${
                selectedProject === project.id 
                  ? 'border-orange-500 bg-orange-50 shadow-lg' 
                  : 'border-gray-300 hover:border-orange-300'
              }`}
              onClick={() => handleProjectSelect(project.id)}
            >
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md">
                  {projectIcons[project.id]}
                </div>
                <CardTitle className="text-xl font-semibold text-gray-900 mb-2">
                  {project.title}
                </CardTitle>
                <CardDescription className="text-gray-700">
                  {project.shortDescription}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <Euro className="h-3 w-3 mr-1" />
                      {project.priceRange}
                    </Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {project.duration}
                    </Badge>
                  </div>
                  
                  <div className="text-center">
                    <Button 
                      variant={selectedProject === project.id ? "default" : "outline"}
                      className={`w-full ${
                        selectedProject === project.id 
                          ? "bg-orange-700 hover:bg-orange-800" 
                          : "border-orange-200 hover:bg-orange-50"
                      }`}
                    >
                      {selectedProject === project.id ? "Projet sélectionné" : "Découvrir ce projet"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Project Details */}
        {selectedProjectData && (
          <div id="project-details" className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 border border-gray-100">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                    {projectIcons[selectedProjectData.id]}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {selectedProjectData.title}
                    </h3>
                    <p className="text-orange-700 font-medium">
                      Projet sélectionné
                    </p>
                  </div>
                </div>

                <p className="text-gray-700 text-lg leading-relaxed">
                  {selectedProjectData.fullDescription}
                </p>

                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 text-lg">
                    Ce qui est inclus dans ce projet :
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {selectedProjectData.includes.map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <Link to="/simulateur" className="flex-1">
                    <Button size="lg" className="w-full bg-orange-700 hover:bg-orange-800">
                      Tester mon éligibilité
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <BoutonRappel
                    className="flex-1 border-2 border-orange-200 hover:bg-orange-50"
                    avecIcone={false}
                  />
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-8">
                <h4 className="text-xl font-bold text-gray-900 mb-6">
                  Exemple de financement
                </h4>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-orange-200">
                    <span className="text-gray-700">Coût estimé du projet</span>
                    <span className="font-semibold text-gray-900">
                      {selectedProjectData.estimatedCost} €
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700">MaPrimeAdapt' (70%)</span>
                      <span className="font-semibold text-green-700">
                        - {Math.round(selectedProjectData.estimatedCost * 0.7)} €
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-green-700">Aides complémentaires</span>
                      <span className="font-semibold text-green-700">
                        - {selectedProjectData.additionalAids} €
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-green-700">AMO (pris en charge)</span>
                      <span className="font-semibold text-green-700">0 €</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t-2 border-orange-300">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Reste à charge</span>
                      <span className="text-2xl font-bold text-orange-700">
                        {selectedProjectData.remainingCost} €
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-white p-4 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800">
                    <strong>Économie réalisée :</strong> {' '}
                    {Math.round(selectedProjectData.estimatedCost * 0.7) + selectedProjectData.additionalAids} € 
                    grâce à notre accompagnement MaPrimeAdapt'
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gray-900 text-white rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Besoin de conseils personnalisés ?
            </h3>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Nos conseillers MaPrimeAdapt' vous accompagnent gratuitement pour définir 
              le projet d'adaptation le plus adapté à vos besoins
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-lg mx-auto">
              <Link to="/simulateur" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="bg-orange-700 hover:bg-orange-800 w-full sm:w-auto"
                >
                  Démarrer ma simulation
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-blue-400 text-white hover:bg-white hover:text-blue-900 w-full sm:w-auto"
                onClick={() => setIsContactModalOpen(true)}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Nous contacter
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-gray-400 text-white hover:bg-white hover:text-gray-900 w-full sm:w-auto"
                onClick={() => window.location.href = 'tel:0939035444'}
              >
                <Phone className="mr-2 h-5 w-5" />
                09 39 03 54 44
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="conseil"
      />
    </section>
  );
};

export default ProjectSelector;